import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SOCKET_PATH = '/api/socket.io';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { user, getSocketToken } = useAuth();
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [dmTypingUsers, setDmTypingUsers] = useState({});
  const [presenceByUser, setPresenceByUser] = useState({});
  const [presenceNow, setPresenceNow] = useState(() => Date.now());
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  const reconnectAttempts = useRef(0);
  const joinedChannelsRef = useRef(new Set());
  const joinedDmsRef = useRef(new Set());
  const typingTimeoutsRef = useRef({});
  // Keep component subscriptions independent of the socket object's creation time.
  // A subscriber may mount while the auth/token request is still in flight; when the
  // socket is created (or recreated for another authenticated session), all currently
  // registered callbacks are attached exactly once.
  const eventSubscriptionsRef = useRef(new Map());

  const subscribeToSocketEvent = useCallback((eventName, callback) => {
    if (!eventName || typeof callback !== 'function') return () => {};

    let callbacks = eventSubscriptionsRef.current.get(eventName);
    if (!callbacks) {
      callbacks = new Set();
      eventSubscriptionsRef.current.set(eventName, callbacks);
    }
    callbacks.add(callback);
    socketRef.current?.on(eventName, callback);

    return () => {
      callbacks.delete(callback);
      socketRef.current?.off(eventName, callback);
      if (callbacks.size === 0) eventSubscriptionsRef.current.delete(eventName);
    };
  }, []);

  const mergePresenceEntries = useCallback((entries, options = {}) => {
    const list = Array.isArray(entries) ? entries : [entries];
    const receivedAt = Date.now();
    const { snapshotRequestedAt = null } = options;

    setPresenceByUser((previousMap) => {
      let changed = false;
      const nextMap = { ...previousMap };

      list.filter(Boolean).forEach((entry) => {
        if (entry.user_id == null) return;
        const userId = String(entry.user_id);
        const previous = previousMap[userId];
        // Do not let a slower snapshot overwrite a realtime event received after
        // that snapshot request began.
        if (
          snapshotRequestedAt != null
          && previous?.updated_at
          && previous.updated_at >= snapshotRequestedAt
        ) {
          return;
        }

        const updated = { ...(previous || {}), user_id: userId };
        // Perf: only count this as a real change when a visible field actually
        // differs. Previously `updated_at` was stamped unconditionally, so EVERY
        // user_online/user_offline event (from ANY user, not just contacts)
        // produced a new presenceByUser object and re-rendered the entire app —
        // sidebar, open chat and every message bubble.
        let fieldChanged = false;
        if (Object.prototype.hasOwnProperty.call(entry, 'is_online')) {
          const nextOnline = Boolean(entry.is_online);
          if (updated.is_online !== nextOnline) {
            updated.is_online = nextOnline;
            fieldChanged = true;
          }
        }
        if (entry.last_seen != null && updated.last_seen !== entry.last_seen) {
          updated.last_seen = entry.last_seen;
          fieldChanged = true;
        }

        if (fieldChanged || !previous) {
          updated.updated_at = receivedAt;
          nextMap[userId] = updated;
          changed = true;
        }
      });

      return changed ? nextMap : previousMap;
    });
  }, []);

  const requestPresenceSnapshot = useCallback(() => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    const snapshotRequestedAt = Date.now();
    socket.emit('get_presence_snapshot', {}, (response) => {
      if (response?.status === 'ok') {
        mergePresenceEntries(response.presence || [], { snapshotRequestedAt });
      }
    });
  }, [mergePresenceEntries]);

  // Idempotent "this user is clearly online" hint. Used for signals that prove
  // activity (a realtime message or a typing event) so a missed presence edge
  // heals immediately instead of staying stale until a refresh. Any later
  // authoritative user_offline event still wins.
  const noteUserOnline = useCallback((userId) => {
    if (userId == null) return;
    mergePresenceEntries({ user_id: userId, is_online: true });
  }, [mergePresenceEntries]);

  const onlineUsers = useMemo(() => new Set(
    Object.entries(presenceByUser)
      .filter(([, presence]) => presence?.is_online)
      .map(([userId]) => userId)
  ), [presenceByUser]);

  const logBootTiming = useCallback((event, extra = {}) => {
    try {
      if (localStorage.getItem('whisper_debug_boot_timing') !== 'true') return;
      console.info('[whisper boot]', event, {
        t: Math.round(performance.now()),
        ...extra,
      });
    } catch {
      // Debug logging should never affect socket startup.
    }
  }, []);

  // Update browser badge
  useEffect(() => {
    if (totalUnreadCount > 0) {
      document.title = `(${totalUnreadCount}) Whisper`;
      if ('setAppBadge' in navigator) {
        navigator.setAppBadge(totalUnreadCount).catch(() => {});
      }
    } else {
      document.title = 'Whisper';
      if ('clearAppBadge' in navigator) {
        navigator.clearAppBadge().catch(() => {});
      }
    }
  }, [totalUnreadCount]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const initSocket = async () => {
      try {
        const socketToken = await getSocketToken();
        if (cancelled) return;

        logBootTiming('socket-connect:start');
        socketRef.current = io(BACKEND_URL, {
          path: SOCKET_PATH,
          // WebSocket FIRST: polling is driven by JS timers, which Chrome
          // throttles to ~1/min in background/minimized tabs — messages then
          // stall until the tab is touched. WebSocket frames are network
          // events and keep arriving while minimized. Polling remains as the
          // fallback for proxies/networks that block WebSocket upgrades.
          transports: ['websocket', 'polling'],
          upgrade: true,
          timeout: 20000,
          withCredentials: true,
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
        });

        eventSubscriptionsRef.current.forEach((callbacks, eventName) => {
          callbacks.forEach((callback) => socketRef.current?.on(eventName, callback));
        });

        socketRef.current.on('connect', () => {
          console.log('Socket connected');
          logBootTiming('socket-connect:success');
          setConnected(true);
          setAuthenticated(false);
          reconnectAttempts.current = 0;

          socketRef.current.emit('authenticate', { token: socketToken }, (response) => {
            if (response?.status === 'ok') {
              console.log('Socket authenticated');
              logBootTiming('socket-auth:success');
              setAuthenticated(true);
              mergePresenceEntries(response.presence || []);

              // Rejoin channels
              joinedChannelsRef.current.forEach((channelId) => {
                socketRef.current.emit('join_channel', { channel_id: channelId });
              });

              // Rejoin DMs
              joinedDmsRef.current.forEach((dmId) => {
                socketRef.current.emit('join_dm', { dm_id: dmId });
              });

              requestPresenceSnapshot();
            } else {
              console.error('Socket auth failed:', response);
            }
          });
        });

        socketRef.current.on('disconnect', (reason) => {
          console.log('Socket disconnected:', reason);
          setConnected(false);
          setAuthenticated(false);
          // Preserve the last snapshot during brief network interruptions. The
          // server grace period and next snapshot establish the final state.
        });

        socketRef.current.on('connect_error', (error) => {
          console.error('Socket connection error:', error);
          reconnectAttempts.current++;
        });

        // Online/Offline status. Coerce ids to String so the Set membership test
        // is stable regardless of how the id arrives. onlineUsers updates globally
        // for every presence event; the open-DM API reconciliation is triggered
        // narrowly in ChatPage (only when the event matches the open participant),
        // so we intentionally do NOT bump presenceRefreshTick here — that tick stays
        // reserved for reconnect / visibility / focus catch-up.
        socketRef.current.on('user_online', (data) => {
          mergePresenceEntries({ ...data, is_online: true });
        });

        socketRef.current.on('user_offline', (data) => {
          mergePresenceEntries({ ...data, is_online: false });
        });

        // Channel typing with auto-clear
        socketRef.current.on('typing_start', (data) => {
          // Typing is proof of activity: heal any stale offline state.
          noteUserOnline(data.user_id);
          setTypingUsers((prev) => ({
            ...prev,
            [data.channel_id]: { user_id: data.user_id, username: data.username },
          }));

          const key = `channel_${data.channel_id}_${data.user_id}`;
          if (typingTimeoutsRef.current[key]) clearTimeout(typingTimeoutsRef.current[key]);
          typingTimeoutsRef.current[key] = setTimeout(() => {
            setTypingUsers((prev) => {
              const s = { ...prev };
              if (s[data.channel_id]?.user_id === data.user_id) delete s[data.channel_id];
              return s;
            });
          }, 3000);
        });

        socketRef.current.on('typing_stop', (data) => {
          const key = `channel_${data.channel_id}_${data.user_id}`;
          if (typingTimeoutsRef.current[key]) {
            clearTimeout(typingTimeoutsRef.current[key]);
            delete typingTimeoutsRef.current[key];
          }
          setTypingUsers((prev) => {
            const s = { ...prev };
            if (s[data.channel_id]?.user_id === data.user_id) delete s[data.channel_id];
            return s;
          });
        });

        // DM typing with auto-clear
        socketRef.current.on('dm_typing_start', (data) => {
          // Typing is proof of activity: heal any stale offline state.
          noteUserOnline(data.user_id);
          setDmTypingUsers((prev) => ({
            ...prev,
            [data.dm_id]: { user_id: data.user_id, username: data.username },
          }));

          const key = `dm_${data.dm_id}_${data.user_id}`;
          if (typingTimeoutsRef.current[key]) clearTimeout(typingTimeoutsRef.current[key]);
          typingTimeoutsRef.current[key] = setTimeout(() => {
            setDmTypingUsers((prev) => {
              const s = { ...prev };
              if (s[data.dm_id]?.user_id === data.user_id) delete s[data.dm_id];
              return s;
            });
          }, 3000);
        });

        socketRef.current.on('dm_typing_stop', (data) => {
          const key = `dm_${data.dm_id}_${data.user_id}`;
          if (typingTimeoutsRef.current[key]) {
            clearTimeout(typingTimeoutsRef.current[key]);
            delete typingTimeoutsRef.current[key];
          }
          setDmTypingUsers((prev) => {
            const s = { ...prev };
            if (s[data.dm_id]?.user_id === data.user_id) delete s[data.dm_id];
            return s;
          });
        });
      } catch (error) {
        console.error('Failed to initialize socket:', error);
      }
    };

    initSocket();

    return () => {
      cancelled = true;
      Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
      typingTimeoutsRef.current = {};
      joinedChannelsRef.current.clear();
      joinedDmsRef.current.clear();
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user?.id, getSocketToken, logBootTiming, mergePresenceEntries, noteUserOnline, requestPresenceSnapshot]);

  // One shared clock refreshes every last-seen label without one timer per row,
  // and every tick requests an authoritative presence snapshot while the tab
  // stays visible. Realtime events cover normal changes instantly; the
  // per-minute snapshot is the safety net that self-heals ANY missed edge
  // (reconnect races, dropped events) within ≤60s — never requiring a refresh.
  useEffect(() => {
    const timer = setInterval(() => {
      setPresenceNow(Date.now());
      if (socketRef.current?.connected) {
        requestPresenceSnapshot();
      }
    }, 60000);
    return () => clearInterval(timer);
  }, [requestPresenceSnapshot]);

  // Recover mobile sockets after focus, visibility, bfcache, or network return and
  // reconcile a scoped snapshot after the reconnect handshake.
  useEffect(() => {
    const reviveIfNeeded = () => {
      const socket = socketRef.current;
      if (socket && !socket.connected) {
        try { socket.connect(); } catch {}
      }
      setPresenceNow(Date.now());
      if (socket?.connected) {
        requestPresenceSnapshot();
        setTimeout(requestPresenceSnapshot, 750);
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        reviveIfNeeded();
      }
    };
    const handleFocus = () => reviveIfNeeded();

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handleFocus);
    window.addEventListener('online', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handleFocus);
      window.removeEventListener('online', handleFocus);
    };
  }, [requestPresenceSnapshot]);

  // Channel functions
  const joinChannel = useCallback(
    (channelId) => {
      if (!channelId) return;
      joinedChannelsRef.current.add(channelId);
      if (socketRef.current?.connected && authenticated) {
        socketRef.current.emit('join_channel', { channel_id: channelId });
      }
    },
    [authenticated]
  );

  const leaveChannel = useCallback((channelId) => {
    if (!channelId) return;
    joinedChannelsRef.current.delete(channelId);
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave_channel', { channel_id: channelId });
    }
  }, []);

  // DM functions
  const joinDm = useCallback(
    (dmId) => {
      if (!dmId) return;
      joinedDmsRef.current.add(dmId);
      if (socketRef.current?.connected && authenticated) {
        socketRef.current.emit('join_dm', { dm_id: dmId });
      }
    },
    [authenticated]
  );

  const leaveDm = useCallback((dmId) => {
    if (!dmId) return;
    joinedDmsRef.current.delete(dmId);
  }, []);

  // Typing functions
  const emitTypingStart = useCallback((channelId, username) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing_start', { channel_id: channelId, username });
    }
  }, []);

  const emitTypingStop = useCallback((channelId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing_stop', { channel_id: channelId });
    }
  }, []);

  const emitDmTypingStart = useCallback((dmId, username) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing_start', { dm_id: dmId, username });
    }
  }, []);

  const emitDmTypingStop = useCallback((dmId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing_stop', { dm_id: dmId });
    }
  }, []);

  // Mark messages seen
  const markMessagesSeen = useCallback((channelId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('mark_messages_seen', { channel_id: channelId });
    }
  }, []);

  // ── Event listener factories ──────────────────────────────────────────────

  const onNewMessage = useCallback((cb) => {
    return subscribeToSocketEvent('new_message', cb);
  }, [subscribeToSocketEvent]);

  const onMessageEdited = useCallback((cb) => {
    return subscribeToSocketEvent('message_edited', cb);
  }, [subscribeToSocketEvent]);

  const onMessageDeleted = useCallback((cb) => {
    return subscribeToSocketEvent('message_deleted', cb);
  }, [subscribeToSocketEvent]);

  const onReactionUpdated = useCallback((cb) => {
    return subscribeToSocketEvent('reaction_updated', cb);
  }, [subscribeToSocketEvent]);

  const onMessageRead = useCallback((cb) => {
    return subscribeToSocketEvent('message_read', cb);
  }, [subscribeToSocketEvent]);

  const onMessagesSeen = useCallback((cb) => {
    return subscribeToSocketEvent('messages_seen', cb);
  }, [subscribeToSocketEvent]);

  const onMessagesRead = useCallback((cb) => {
    return subscribeToSocketEvent('messages_read', cb);
  }, [subscribeToSocketEvent]);

  const onChannelDeleted = useCallback((cb) => {
    return subscribeToSocketEvent('channel_deleted', cb);
  }, [subscribeToSocketEvent]);

  const onNewDmMessage = useCallback((cb) => {
    return subscribeToSocketEvent('new_dm_message', cb);
  }, [subscribeToSocketEvent]);

  const onDmMessageEdited = useCallback((cb) => {
    return subscribeToSocketEvent('dm_message_edited', cb);
  }, [subscribeToSocketEvent]);

  const onDmMessageDeleted = useCallback((cb) => {
    return subscribeToSocketEvent('dm_message_deleted', cb);
  }, [subscribeToSocketEvent]);

  const onDmReactionUpdated = useCallback((cb) => {
    return subscribeToSocketEvent('dm_reaction_updated', cb);
  }, [subscribeToSocketEvent]);

  const onDmMessagesSeen = useCallback((cb) => {
    return subscribeToSocketEvent('dm_messages_seen', cb);
  }, [subscribeToSocketEvent]);

  const onDmMessagesDelivered = useCallback((cb) => {
    return subscribeToSocketEvent('dm_messages_delivered', cb);
  }, [subscribeToSocketEvent]);

  const onDmUnreadUpdate = useCallback((cb) => {
    return subscribeToSocketEvent('dm_unread_update', cb);
  }, [subscribeToSocketEvent]);

  const onDmCreated = useCallback((cb) => {
    return subscribeToSocketEvent('dm_created', cb);
  }, [subscribeToSocketEvent]);

  const onUserOnline = useCallback((cb) => {
    return subscribeToSocketEvent('user_online', cb);
  }, [subscribeToSocketEvent]);

  const onUserOffline = useCallback((cb) => {
    return subscribeToSocketEvent('user_offline', cb);
  }, [subscribeToSocketEvent]);

  const onUserProfileUpdated = useCallback((cb) => {
    return subscribeToSocketEvent('user_profile_updated', cb);
  }, [subscribeToSocketEvent]);

  const contextValue = useMemo(() => ({
    socket: socketRef.current,
    connected,
    authenticated,
    typingUsers,
    dmTypingUsers,
    onlineUsers,
    presenceByUser,
    presenceNow,
    requestPresenceSnapshot,
    noteUserOnline,
    totalUnreadCount,
    setTotalUnreadCount,
    joinChannel,
    leaveChannel,
    joinDm,
    leaveDm,
    emitTypingStart,
    emitTypingStop,
    emitDmTypingStart,
    emitDmTypingStop,
    markMessagesSeen,
    onNewMessage,
    onMessageEdited,
    onMessageDeleted,
    onReactionUpdated,
    onMessageRead,
    onMessagesSeen,
    onMessagesRead,
    onChannelDeleted,
    onNewDmMessage,
    onDmMessageEdited,
    onDmMessageDeleted,
    onDmReactionUpdated,
    onDmMessagesSeen,
    onDmMessagesDelivered,
    onDmUnreadUpdate,
    onDmCreated,
    onUserOnline,
    onUserOffline,
    onUserProfileUpdated,
  }), [
    connected,
    authenticated,
    typingUsers,
    dmTypingUsers,
    onlineUsers,
    presenceByUser,
    presenceNow,
    requestPresenceSnapshot,
    noteUserOnline,
    totalUnreadCount,
    joinChannel,
    leaveChannel,
    joinDm,
    leaveDm,
    emitTypingStart,
    emitTypingStop,
    emitDmTypingStart,
    emitDmTypingStop,
    markMessagesSeen,
    onNewMessage,
    onMessageEdited,
    onMessageDeleted,
    onReactionUpdated,
    onMessageRead,
    onMessagesSeen,
    onMessagesRead,
    onChannelDeleted,
    onNewDmMessage,
    onDmMessageEdited,
    onDmMessageDeleted,
    onDmReactionUpdated,
    onDmMessagesSeen,
    onDmMessagesDelivered,
    onDmUnreadUpdate,
    onDmCreated,
    onUserOnline,
    onUserOffline,
    onUserProfileUpdated,
  ]);

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};
